import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Plus, Minus, X, MessageCircle, Sparkles, Truck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { formatRupiah } from "../i18n/content";
import { api, imageUrl } from "../lib/api";
import { Checkout } from "./Checkout";

const IMAGES = {
  small:  "/img/product/1.jpeg",
  medium: "/img/product/2.jpeg",
  pack3:  "/img/product/3.jpeg",
  kilo:   "/img/product/1.jpeg",
  box6kg: "/img/product/2.jpeg",
};

export const Shop = () => {
  const { t, lang } = useLang();
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((response) => setProducts(response.data.data || [])).catch(() => setProducts([]));
  }, []);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) =>
    setCart((c) => {
      const next = { ...c, [id]: Math.max(0, (c[id] || 0) - 1) };
      if (next[id] === 0) delete next[id];
      return next;
    });
  const remove = (id) =>
    setCart((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });

  const totalQty = Object.values(cart).reduce((a, b) => a + b, 0);
  const totalPrice = products.reduce(
    (sum, it) => sum + (cart[it.id] || 0) * it.price,
    0,
  );

  return (
    <section
      id="shop"
      className="relative bg-brand-cream-alt py-24 md:py-32"
      data-testid="shop-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-brand-red" />
              <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-brand-red">
                {t.shop.label}
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-brand-ink leading-[0.95]">
              {t.shop.heading}
              <br />
              <span className="italic text-brand-red">{t.shop.heading2}</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="font-sans text-base text-brand-muted leading-relaxed">{t.shop.sub}</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {products.map((item, i) => {
            const qty = cart[item.id] || 0;
            return (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group bg-brand-cream border border-brand-ink/10 flex flex-col hover:border-brand-red/40 transition-all duration-500"
                data-testid={`shop-product-${item.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={imageUrl(item.image) || IMAGES[{ "BP-SMALL": "small", "BP-MEDIUM": "medium", "BP-PACK3": "pack3", "BP-KILO": "kilo", "BP-BOX6": "box6kg" }[item.sku]]}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                    {item.tag && (
                    <span className="absolute top-3 left-3 px-3 py-1 bg-brand-yellow text-brand-ink text-[10px] tracking-[0.2em] uppercase font-medium">
                      {item.tag}
                    </span>
                  )}
                  <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-ink/85 backdrop-blur text-brand-cream text-[10px] tracking-[0.15em] uppercase">
                    <Sparkles className="w-3 h-3 text-brand-yellow" />
                    {t.shop.ready}
                  </span>
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-xl md:text-2xl text-brand-ink leading-tight">
                      {item.name}
                    </h3>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-brand-muted shrink-0">
                      {item.weight_grams} g
                    </span>
                  </div>
                  <p className="mt-3 font-sans text-sm text-brand-muted leading-relaxed flex-1">
                    {item.description}
                  </p>

                  <div className="mt-6 flex items-end justify-between gap-4">
                    <div>
                      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-brand-muted">
                        Price
                      </div>
                      <div
                        className="font-serif text-2xl md:text-3xl text-brand-red"
                        data-testid={`shop-price-${item.id}`}
                      >
                        {formatRupiah(item.price)}
                      </div>
                    </div>
                    {qty === 0 ? (
                      <button
                        onClick={() => add(item.id)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-ink text-brand-cream hover:bg-brand-red font-sans text-xs tracking-[0.15em] uppercase transition-colors"
                        data-testid={`shop-add-${item.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t.shop.addCart}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 border border-brand-ink/20 bg-brand-cream">
                        <button
                          onClick={() => sub(item.id)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-brand-cream-alt"
                          data-testid={`shop-sub-${item.id}`}
                          aria-label="Decrease"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                          className="w-6 text-center font-sans text-sm font-medium"
                          data-testid={`shop-qty-${item.id}`}
                        >
                          {qty}
                        </span>
                        <button
                          onClick={() => add(item.id)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-brand-cream-alt"
                          data-testid={`shop-inc-${item.id}`}
                          aria-label="Increase"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-10 flex items-start gap-3 text-brand-muted">
          <Truck className="w-5 h-5 shrink-0 mt-0.5 text-brand-red" />
          <p className="font-sans text-sm leading-relaxed max-w-3xl">{t.shop.shippingNote}</p>
        </div>
      </div>

      {/* Floating Cart Button */}
      <AnimatePresence>
        {totalQty > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 left-6 z-40 flex items-center gap-3 px-5 py-3 bg-brand-red text-brand-cream rounded-full shadow-2xl shadow-brand-red/30 hover:bg-brand-red-dark transition-colors"
            data-testid="cart-fab"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="font-sans text-sm font-medium" data-testid="cart-fab-count">
              {totalQty} · {formatRupiah(totalPrice)}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-brand-ink/60 backdrop-blur-sm z-50"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-brand-cream z-50 flex flex-col"
              data-testid="cart-drawer"
            >
              <header className="flex items-center justify-between p-6 border-b border-brand-ink/10">
                <h3 className="font-serif text-2xl text-brand-ink">
                  {lang === "id" ? "Keranjang" : "Your Cart"}
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 flex items-center justify-center border border-brand-ink/20 rounded-full"
                  data-testid="cart-close"
                  aria-label="Close cart"
                >
                  <X className="w-4 h-4" />
                </button>
              </header>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {products.filter((it) => cart[it.id]).map((it) => (
                  <div
                    key={it.id}
                    className="flex gap-4 pb-4 border-b border-brand-ink/10"
                    data-testid={`cart-line-${it.id}`}
                  >
                    <img src={imageUrl(it.image) || IMAGES[{ "BP-SMALL": "small", "BP-MEDIUM": "medium", "BP-PACK3": "pack3", "BP-KILO": "kilo", "BP-BOX6": "box6kg" }[it.sku]]} alt="" className="w-20 h-20 object-cover" />
                    <div className="flex-1">
                      <div className="font-serif text-base text-brand-ink leading-snug">{it.name}</div>
                      <div className="font-sans text-xs text-brand-muted mt-1">{it.weight_grams} g</div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 border border-brand-ink/20">
                          <button onClick={() => sub(it.id)} className="w-7 h-7 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                          <span className="w-5 text-center text-sm">{cart[it.id]}</span>
                          <button onClick={() => add(it.id)} className="w-7 h-7 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                        </div>
                        <button onClick={() => remove(it.id)} className="text-xs text-brand-muted hover:text-brand-red">
                          {lang === "id" ? "Hapus" : "Remove"}
                        </button>
                      </div>
                      <div className="mt-2 font-serif text-brand-red">{formatRupiah(it.price * cart[it.id])}</div>
                    </div>
                  </div>
                ))}
              </div>
              <footer className="p-6 border-t border-brand-ink/10 space-y-4">
                <div className="flex items-baseline justify-between">
                  <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-brand-muted">Total</span>
                  <span className="font-serif text-3xl text-brand-ink" data-testid="cart-total">
                    {formatRupiah(totalPrice)}
                  </span>
                </div>
                <a
                  href="#checkout"
                  onClick={(event) => { event.preventDefault(); setOpen(false); setCheckout(true); }}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-brand-red text-brand-cream hover:bg-brand-red-dark font-sans text-sm tracking-[0.2em] uppercase transition-colors"
                  data-testid="cart-checkout"
                >
                  <MessageCircle className="w-4 h-4" />
                  {lang === "id" ? "Checkout & Bayar" : "Checkout & Pay"}
                </a>
                <p className="text-xs text-brand-muted text-center">
                  {lang === "id"
                    ? "Ongkir dihitung otomatis dan pembayaran diproses aman melalui Midtrans."
                    : "Shipping is calculated automatically and payment is processed securely by Midtrans."}
                </p>
              </footer>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      {checkout && <Checkout products={products} cart={cart} onClose={() => setCheckout(false)} />}
    </section>
  );
};
